/**
 * Section 5A — Analytics report cards.
 *
 * Six pre-built reports (turnover, time-to-hire, diversity, compensation,
 * compliance, onboarding) rendered as self-contained cards. A shared
 * date-range + department filter bar sits at the top; each card pulls
 * its own data on mount and whenever filters change, and exposes a CSV
 * export button backed by the server.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TrendingDown,
  Timer,
  Users,
  DollarSign,
  ShieldCheck,
  ClipboardCheck,
  Download,
  Loader2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useToast } from "@/hooks/useToast";
import {
  downloadBlob,
  exportPrebuiltReport,
  getComplianceReport,
  getCompensationReport,
  getDiversityReport,
  getOnboardingReport,
  getTimeToHireReport,
  getTurnoverReport,
  type CompensationReport,
  type ComplianceReport,
  type DiversityReport,
  type OnboardingReport,
  type PrebuiltReportKey,
  type ReportFilters,
  type TimeToHireReport,
  type TurnoverReport,
} from "@/api/reports";
import { listDepartments } from "@/api/organizations";
import type { DepartmentFull } from "@/types/organization";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";

function formatCurrency(n: number | null): string {
  if (n == null) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function BarRow({
  label,
  value,
  max,
  color = "bg-accent",
}: {
  label: string;
  value: number;
  max: number;
  color?: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-text-primary truncate">{label}</span>
        <span className="text-text-muted">{value}</span>
      </div>
      <div className="h-2 bg-background rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function AnalyticsReports() {
  const { addToast } = useToast();
  const [filters, setFilters] = useState<ReportFilters>({});
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);

  useEffect(() => {
    listDepartments()
      .then(setDepartments)
      .catch(() => undefined);
  }, []);

  const deptOptions = useMemo(
    () => [
      { value: "", label: "All departments" },
      ...departments.map((d) => ({ value: d.id, label: d.name })),
    ],
    [departments],
  );

  const exportCsv = async (key: PrebuiltReportKey) => {
    try {
      const { blob, filename } = await exportPrebuiltReport(key, filters);
      downloadBlob(blob, filename);
    } catch {
      addToast("error", "Couldn't export report");
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            type="date"
            label="Start date"
            value={filters.start_date ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                start_date: e.target.value || undefined,
              }))
            }
          />
          <Input
            type="date"
            label="End date"
            value={filters.end_date ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                end_date: e.target.value || undefined,
              }))
            }
          />
          <Select
            label="Department"
            value={filters.department ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                department: e.target.value || undefined,
              }))
            }
            options={deptOptions}
          />
          <div className="flex items-end">
            <Button
              variant="secondary"
              onClick={() => setFilters({})}
              className="w-full"
            >
              Clear filters
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TurnoverCard filters={filters} onExport={() => exportCsv("turnover")} />
        <TimeToHireCard
          filters={filters}
          onExport={() => exportCsv("time-to-hire")}
        />
        <DiversityCard
          filters={filters}
          onExport={() => exportCsv("diversity")}
        />
        <CompensationCard
          filters={filters}
          onExport={() => exportCsv("compensation")}
        />
        <ComplianceCard
          filters={filters}
          onExport={() => exportCsv("compliance")}
        />
        <OnboardingCard
          filters={filters}
          onExport={() => exportCsv("onboarding")}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic card wrapper with a shared header
// ---------------------------------------------------------------------------

function ReportCard({
  title,
  Icon,
  subtitle,
  onExport,
  loading,
  children,
}: {
  title: string;
  Icon: LucideIcon;
  subtitle?: string;
  onExport: () => void;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-2">
          <Icon className="h-4 w-4 text-accent mt-0.5" />
          <div>
            <h3 className="font-semibold font-heading text-text-primary">
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <button
          onClick={onExport}
          className="p-1.5 rounded hover:bg-background/70 text-text-muted hover:text-text-primary"
          title="Export CSV"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-32 text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

function useReportData<T>(
  fetcher: (filters: ReportFilters) => Promise<T>,
  filters: ReportFilters,
  label: string,
): { data: T | null; loading: boolean } {
  const { addToast } = useToast();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetcher(filters);
      setData(d);
    } catch {
      addToast("error", `Couldn't load ${label}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading };
}

// ---------------------------------------------------------------------------
// Individual cards
// ---------------------------------------------------------------------------

function TurnoverCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<TurnoverReport>(
    getTurnoverReport,
    filters,
    "turnover report",
  );
  const max = Math.max(1, ...(data?.by_department.map((d) => d.count) ?? []));
  return (
    <ReportCard
      title="Turnover"
      Icon={TrendingDown}
      subtitle={
        data
          ? `${data.total_terminations} terminations · ${data.turnover_rate_pct}% rate`
          : undefined
      }
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-text-muted">Rate</p>
              <p className="text-xl font-semibold text-text-primary">
                {data.turnover_rate_pct}%
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Leavers</p>
              <p className="text-xl font-semibold text-text-primary">
                {data.total_terminations}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Avg HC</p>
              <p className="text-xl font-semibold text-text-primary">
                {data.avg_headcount}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider">
              By department
            </p>
            {data.by_department.length === 0 ? (
              <p className="text-sm text-text-muted">No terminations in window</p>
            ) : (
              data.by_department
                .slice(0, 6)
                .map((d) => (
                  <BarRow
                    key={d.name}
                    label={d.name}
                    value={d.count}
                    max={max}
                    color="bg-danger"
                  />
                ))
            )}
          </div>
        </div>
      )}
    </ReportCard>
  );
}

function TimeToHireCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<TimeToHireReport>(
    getTimeToHireReport,
    filters,
    "time-to-hire report",
  );
  const max = Math.max(1, ...(data?.by_department.map((d) => d.avg_days) ?? []));
  return (
    <ReportCard
      title="Time to Hire"
      Icon={Timer}
      subtitle={
        data ? `${data.total_hires} hires · avg ${data.avg_days} days` : undefined
      }
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-sm">
            {["avg_days", "median_days", "min_days", "max_days"].map((k) => (
              <div key={k}>
                <p className="text-xs text-text-muted capitalize">
                  {k.replace("_days", "").replace("_", " ")}
                </p>
                <p className="text-lg font-semibold text-text-primary">
                  {(data as any)[k]}d
                </p>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider">
              Avg days by department
            </p>
            {data.by_department.length === 0 ? (
              <p className="text-sm text-text-muted">No accepted offers in window</p>
            ) : (
              data.by_department
                .slice(0, 6)
                .map((d) => (
                  <BarRow
                    key={d.name}
                    label={`${d.name} (${d.hires})`}
                    value={d.avg_days}
                    max={max}
                  />
                ))
            )}
          </div>
        </div>
      )}
    </ReportCard>
  );
}

function DiversityCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<DiversityReport>(
    getDiversityReport,
    filters,
    "diversity report",
  );
  const max = Math.max(1, ...(data?.by_tenure.map((d) => d.count) ?? []));
  return (
    <ReportCard
      title="Diversity & Tenure"
      Icon={Users}
      subtitle={data ? `${data.total} active employees` : undefined}
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Tenure distribution
            </p>
            <div className="space-y-2">
              {data.by_tenure.map((b) => (
                <BarRow
                  key={b.bucket}
                  label={b.bucket}
                  value={b.count}
                  max={max}
                  color="bg-accent"
                />
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.by_employment_type.map((t) => (
              <Badge key={t.type} variant="default">
                {t.type}: {t.count}
              </Badge>
            ))}
          </div>
          {data.notes?.length > 0 && (
            <p className="text-xs text-text-muted italic">{data.notes[0]}</p>
          )}
        </div>
      )}
    </ReportCard>
  );
}

function CompensationCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<CompensationReport>(
    getCompensationReport,
    filters,
    "compensation report",
  );
  return (
    <ReportCard
      title="Compensation"
      Icon={DollarSign}
      subtitle={
        data
          ? `Avg ${formatCurrency(data.overall.avg)} · ${data.bands_covered} bands`
          : undefined
      }
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-sm">
            {(["min", "median", "avg", "max"] as const).map((k) => (
              <div key={k}>
                <p className="text-xs text-text-muted capitalize">{k}</p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatCurrency(data.overall[k])}
                </p>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
              By title
            </p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {data.by_title.slice(0, 10).map((t) => (
                <div
                  key={t.title}
                  className="flex items-center justify-between text-sm py-1"
                >
                  <span className="text-text-primary truncate">
                    {t.title}
                    <span className="text-text-muted ml-1">({t.count})</span>
                  </span>
                  <span className="text-text-muted">
                    {formatCurrency(t.avg)}
                    {t.band_mid != null && (
                      <span className="ml-1 text-xs">
                        / {formatCurrency(t.band_mid)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </ReportCard>
  );
}

function ComplianceCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<ComplianceReport>(
    getComplianceReport,
    filters,
    "compliance report",
  );
  return (
    <ReportCard
      title="Compliance"
      Icon={ShieldCheck}
      subtitle={
        data
          ? `${data.total_assignments} assignments · ${data.overdue} overdue`
          : undefined
      }
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {data.by_status.map((s) => (
              <Badge
                key={s.status}
                variant={
                  s.status === "completed"
                    ? "success"
                    : s.status === "expired"
                      ? "danger"
                      : "default"
                }
              >
                {s.status}: {s.count}
              </Badge>
            ))}
          </div>
          <div>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Completion by requirement
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {data.requirements.slice(0, 8).map((r) => (
                <div key={r.id}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-text-primary truncate">
                      {r.name}
                    </span>
                    <span className="text-text-muted">
                      {r.completed}/{r.total} ({r.completion_rate_pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-success rounded-full"
                      style={{ width: `${r.completion_rate_pct}%` }}
                    />
                  </div>
                </div>
              ))}
              {data.requirements.length === 0 && (
                <p className="text-sm text-text-muted">No active requirements</p>
              )}
            </div>
          </div>
        </div>
      )}
    </ReportCard>
  );
}

function OnboardingCard({
  filters,
  onExport,
}: {
  filters: ReportFilters;
  onExport: () => void;
}) {
  const { data, loading } = useReportData<OnboardingReport>(
    getOnboardingReport,
    filters,
    "onboarding report",
  );
  return (
    <ReportCard
      title="Onboarding"
      Icon={ClipboardCheck}
      subtitle={
        data
          ? `${data.total_instances} instances · ${data.completion_rate_pct}% complete`
          : undefined
      }
      onExport={onExport}
      loading={loading}
    >
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-text-muted">Complete %</p>
              <p className="text-xl font-semibold text-success">
                {data.completion_rate_pct}%
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Tasks done</p>
              <p className="text-xl font-semibold text-text-primary">
                {data.task_completion_rate_pct}%
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Avg days</p>
              <p className="text-xl font-semibold text-text-primary">
                {data.avg_days_to_complete}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.by_status.map((s) => (
              <Badge
                key={s.status}
                variant={
                  s.status === "COMPLETED"
                    ? "success"
                    : s.status === "CANCELLED"
                      ? "danger"
                      : "info"
                }
              >
                {s.status}: {s.count}
              </Badge>
            ))}
          </div>
          {data.total_instances === 0 && (
            <p className="text-sm text-text-muted italic">
              No onboarding started in this window.
            </p>
          )}
        </div>
      )}
    </ReportCard>
  );
}
